<template>
  <div class="auto-reply-panel">
    <!-- 标题栏 -->
    <div class="auto-reply-header">
      <span>自动回复</span>
      <el-button
        icon="el-icon-plus"
        size="small"
        @click="openAddDialog"
      ></el-button>
    </div>

    <!-- 话术列表 -->
    <el-scrollbar class="auto-reply-scroll">
      <div class="auto-reply-list">
        <div
          v-for="script in chatStore.autoReplyScripts"
          :key="script.id"
          class="auto-reply-item"
          :class="{ disabled: !script.enabled }"
        >
          <div class="auto-reply-content" @click="showDetail(script)">
            <div class="script-summary">
              <span
                v-for="(m, i) in scriptMessages(script)"
                :key="i"
                class="msg-chip"
              >
                <i :class="msgIcon(m)"></i>{{ msgPreview(m) }}
              </span>
            </div>
          </div>
          <div class="auto-reply-item-actions">
            <el-switch
              v-model="script.enabled"
              @change="toggleEnabled(script)"
              size="small"
            ></el-switch>
            <el-button
              icon="el-icon-edit"
              circle
              size="mini"
              @click.stop="editScript(script)"
            ></el-button>
            <el-button
              icon="el-icon-delete"
              circle
              size="mini"
              @click.stop="deleteScript(script.id)"
            ></el-button>
          </div>
        </div>
        <div v-if="chatStore.autoReplyScripts.length === 0" class="empty-state">
          <i class="el-icon-chat-line-round"></i>
          <p>暂无自动回复话术</p>
          <el-button type="text" @click="openAddDialog">点击添加</el-button>
        </div>
      </div>
    </el-scrollbar>

    <!-- 消息序列编辑器（与超时跟进共用的共享组件） -->
    <auto-reply-messages-editor
      :visible.sync="showAddDialog"
      :title="isEditing ? '编辑自动回复话术' : '添加自动回复话术'"
      :messages="editorMessages"
      :saving="saving"
      @confirm="onEditorConfirm"
    />
  </div>
</template>

<script>
import { addAutoReplyScript, updateAutoReplyScript, deleteAutoReplyScript as deleteApi } from '../../api/autoReplyScript.js'
import { scriptMessages, msgIcon, msgPreview } from '../../utils/autoreplyMessages.js'
import AutoReplyMessagesEditor from './AutoReplyMessagesEditor.vue'

export default {
  name: 'AutoReplyPanel',
  components: { AutoReplyMessagesEditor },
  data() {
    return {
      showAddDialog: false,
      isEditing: false,
      editingId: null,
      editorMessages: [],
      saving: false
    }
  },
  methods: {
    scriptMessages,
    msgIcon,
    msgPreview,
    // ===== 列表操作 =====
    showDetail(script) {
      const lines = this.scriptMessages(script)
        .map((m, i) => `${i + 1}. ${this.msgPreview(m, true)}`)
        .join('\n')
      this.$msgbox({
        title: '话术内容',
        message: this.$createElement('div', {
          style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '360px', overflowY: 'auto', fontSize: '14px', lineHeight: '1.6' }
        }, lines || '（空）'),
        confirmButtonText: '关闭'
      }).catch(() => {})
    },
    toggleEnabled(script) {
      updateAutoReplyScript({ id: script.id, enabled: script.enabled }).catch(() => {
        script.enabled = !script.enabled
        this.$message.error('更新失败')
      })
    },
    deleteScript(id) {
      this.$confirm('确定要删除这条自动回复话术吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteApi({ id }).then(() => {
          this.chatStore.deleteAutoReplyScript(id)
          this.$message.success('删除成功')
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    },
    // ===== 编辑器 =====
    openAddDialog() {
      this.resetForm()
      this.showAddDialog = true
    },
    editScript(script) {
      this.resetForm()
      this.isEditing = true
      this.editingId = script.id
      this.editorMessages = this.scriptMessages(script).map(m => ({ ...m }))
      this.showAddDialog = true
    },
    onEditorConfirm(messages) {
      if (this.saving) return
      this.saving = true
      if (this.isEditing) {
        updateAutoReplyScript({ id: this.editingId, messages }).then(() => {
          this.chatStore.updateAutoReplyScript({ id: this.editingId, messages })
          this.$message.success('更新成功')
          this.showAddDialog = false
          this.resetForm()
        }).catch(() => {
          this.$message.error('保存失败')
        }).finally(() => {
          this.saving = false
        })
      } else {
        addAutoReplyScript({ messages }).then((data) => {
          this.chatStore.addAutoReplyScript(data)
          this.$message.success('添加成功')
          this.showAddDialog = false
          this.resetForm()
        }).catch(() => {
          this.$message.error('保存失败')
        }).finally(() => {
          this.saving = false
        })
      }
    },
    resetForm() {
      this.editorMessages = []
      this.saving = false
      this.isEditing = false
      this.editingId = null
    }
  }
}
</script>

<style lang="scss" scoped>
.auto-reply-panel {
  height: 100%;
  display: flex;
  flex-direction: column;

  .auto-reply-header {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 15px;
    font-weight: bold;
    color: #333;
    border-bottom: 1px solid #e6e6e6;
    background: #fff;
  }

  .auto-reply-scroll {
    flex: 1;
    padding: 10px;
  }

  .auto-reply-list {
    .auto-reply-item {
      display: flex;
      align-items: center;
      padding: 12px;
      margin-bottom: 8px;
      background: #fff;
      border-radius: 6px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border-left: 3px solid #67c23a;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        transform: translateY(-1px);

        .auto-reply-item-actions {
          opacity: 1;
        }
      }

      &.disabled {
        opacity: 0.6;
        border-left-color: #c0c4cc;
      }

      .auto-reply-content {
        flex: 1;
        min-width: 0;
        cursor: pointer;

        .script-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;

          .msg-chip {
            display: inline-flex;
            align-items: center;
            max-width: 100%;
            padding: 2px 8px;
            font-size: 12px;
            line-height: 1.6;
            color: #333;
            background: #f4f6f8;
            border-radius: 10px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            i {
              margin-right: 4px;
              color: #67c23a;
              font-size: 13px;
              flex-shrink: 0;
            }
          }
        }
      }

      .auto-reply-item-actions {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-shrink: 0;
        margin-left: 10px;
        opacity: 0;
        transition: opacity 0.2s ease;

        .el-button {
          margin-left: 0;
          padding: 4px;
          min-width: auto;

          &:hover {
            background: #409EFF;
            color: white;
          }
        }
      }
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #999;

      i { font-size: 40px; margin-bottom: 10px; display: block; }
      p { margin-bottom: 10px; font-size: 14px; }

      .el-button {
        color: #409EFF;
        font-size: 13px;

        &:hover {
          color: #66b1ff;
        }
      }
    }
  }
}
</style>
